import * as React from "react";
import {toast} from 'react-toastify';
import {APIClient} from "../../../utils/APIClient";
import FacebookLogin from '@greatsumini/react-facebook-login';
import {facebookAppId} from "../../../settings/settings";
import {API_URL} from "../../../settings/local_settings";
import {FaIcon} from "../../FaIcon";
import { FACEBOOK_CHANNEL } from '../../channels/channels';
import { withTranslation } from 'react-i18next';
import {oauthDestination} from '../../onboarding/oauthReturn';
import {forgetReconnect, peekReconnect} from '../connections/reconnectNote';
import {
    deferReconnectNotice, destinationAfterConnect, ORIGIN_ACCOUNT, ORIGIN_CREDENTIAL, ORIGIN_NEW, reconnectNotice,
    reconnectResultOf, recordReconnectResult,
} from '../connections/reconnectAccountText';

// Códigos tipados que devuelve StoreFacebookAccessToken. Cualquier otro cae al mensaje genérico: el
// `error_message` crudo del backend no se muestra nunca. Lo único del backend que llega a pantalla es
// `existing_label`, y está acotado a la agencia (el comando lo busca scopeado; para un cliente del
// Portal el conflicto sale como CONNECTION_NOT_AVAILABLE, sin label).
const KNOWN_ERROR_CODES = [
    'FACEBOOK_USER_ALREADY_CONNECTED',
    'CREDENTIAL_LABEL_TAKEN',
    'RECONNECT_TARGET_NOT_FOUND',
    'FACEBOOK_TOKEN_EXCHANGE_FAILED',
];


export class FacebookLinkComponentClass extends React.Component {
    constructor(props) {
        super(props);

        this.deleteAgencyConnection = this.deleteAgencyConnection.bind(this);
        this.responseFacebook = this.responseFacebook.bind(this);
        this.failFacebook = this.failFacebook.bind(this);
    }

    responseFacebook(response) {
        const { t, reconnectAccId } = this.props;
        const credential_label = localStorage.getItem('pending_credential_label');
        localStorage.removeItem('pending_credential_label');
        const payload = {...response, credential_label: credential_label};
        // Sin esto el backend no puede distinguir "reconectar esta conexión" de "agregar una nueva",
        // y una segunda conexión terminaba renombrando a la primera.
        if (reconnectAccId) {
            payload.reconnect_acc_id = reconnectAccId;
        }
        // La cuenta que se arregla desde su «Reconectar» (contrato §2.1): hasta la tanda «Reconexión de
        // pauta» Meta no la leía nunca. Se LEE sin borrarla: si el pedido falla, el modal de Meta sigue
        // abierto y el reintento tiene que llevarla (C-rp-fe-01). La borran el éxito y cerrar el modal.
        const reconnect = peekReconnect(FACEBOOK_CHANNEL);
        if (reconnect && reconnect.adAccountId) {
            payload.reconnect_ad_account_id = reconnect.adAccountId;
        }
        let origin = ORIGIN_NEW;
        if (reconnect && reconnect.adAccountId) origin = ORIGIN_ACCOUNT;
        else if (reconnectAccId) origin = ORIGIN_CREDENTIAL;
        new APIClient(API_URL + 'store_facebook_access_token/').call('POST', payload)
            .then((data) => {
                if (data.status === 'OK') {
                    forgetReconnect();
                    const result = reconnectResultOf(data);
                    recordReconnectResult(FACEBOOK_CHANNEL, result, origin);
                    // La página se recarga y se llevaría el aviso: espera a que Conexiones lo muestre.
                    deferReconnectNotice(reconnectNotice(t, FACEBOOK_CHANNEL, result, origin));
                    // Sin alert bloqueante: la pantalla de destino ya muestra la conexión nueva y el
                    // modal de líneas de control (`?just_connected`).
                    //
                    // Desde acá el destino es SIEMPRE la pantalla clásica. El túnel dejó de depender
                    // de la nota de retorno para Meta —decide su destino en `PlatformCard`, porque
                    // el popup nunca sale del paso—, así que `oauthDestination()` no tiene nada que
                    // encontrar: desde `/agency/*` el guard de contexto la descarta igual. Se
                    // conserva la llamada porque además LIMPIA una nota que haya quedado viva de un
                    // recorrido abandonado, que es justo lo que no debe desviar esta conexión.
                    //
                    // Con el `connection_id` que devuelve el backend, Conexiones abre ESE acceso con
                    // sus cuentas a la vista, como TikTok (cc#209).
                    // Con un backend viejo, que no movía la cuenta, Conexiones la reasigna como antes.
                    window.location = oauthDestination(destinationAfterConnect(FACEBOOK_CHANNEL, data, reconnect));
                } else {
                    toast.error(this.errorMessageFor(data));
                }
            })
            .catch(() => {
                toast.error(t('facebook_link.error.generic'));
            });
    }

    errorMessageFor(data) {
        const { t } = this.props;
        const code = data.error_code || data.status;
        if (!KNOWN_ERROR_CODES.includes(code)) {
            return t('facebook_link.error.generic');
        }
        const label = this.existingLabelFrom(data);
        // `AgencyChannelCredential._label` es nullable: sin variante propia, una conexión histórica sin
        // nombre produciría «» en el medio de la frase.
        if (code === 'FACEBOOK_USER_ALREADY_CONNECTED' && !label) {
            return t('facebook_link.error.FACEBOOK_USER_ALREADY_CONNECTED_unnamed');
        }
        return t('facebook_link.error.' + code, {
            label: label,
            interpolation: {escapeValue: false},
        });
    }

    existingLabelFrom(data) {
        // `errors` viaja como string JSON (ErrorResponse hace json.dumps).
        try {
            const extra = typeof data.errors === 'string' ? JSON.parse(data.errors) : (data.errors || {});
            return extra.existing_label || '';
        } catch (e) {
            return '';
        }
    }

    failFacebook() {
        // Cancelar el diálogo de Facebook dejaba al usuario mirando el modal sin ninguna señal.
        // Va como info, no como error: cancelar es una decisión del usuario, no una falla.
        const { t } = this.props;
        // La reconexión anotada NO se borra: el modal de Meta queda abierto y el reintento la necesita
        // (C-rp-fe-01). La borra cerrar el modal (`FacebookReconnectModal`) o arrancar una conexión nueva.
        toast.info(t('facebook_link.error.cancelled'));
    }

    deleteAgencyConnection() {
        new APIClient(API_URL + 'agency/facebook-connection/delete/').call('POST')
            .then(() => {
                this.props.onConnectionDeleted()
            }).catch(() => {
                toast.error(this.props.t('facebook_link.error.generic'));
            })
    }

    render() {
        const { t } = this.props;
        if (this.props.hasConnection === false) {
            // auth_type: 'reauthenticate' fuerza a Facebook a preguntar con qué cuenta entrar. Sin esto,
            // con sesión activa en facebook.com el SDK resuelve el login solo y devuelve SIEMPRE el mismo
            // userID → era imposible agregar una segunda conexión de Meta desde el mismo navegador.
            return (
                <FacebookLogin
                    appId={facebookAppId}
                    scope="ads_management,ads_read,business_management"
                    loginOptions={{auth_type: 'reauthenticate'}}
                    onSuccess={this.responseFacebook}
                    onFail={this.failFacebook}
                    className="btn facebook"
                >
                    <FaIcon prefix="fab" iconName="facebook-f"/> {t('facebook_link.connect_facebook_ads')}
                </FacebookLogin>
            )
        } else {
            return (
                <button className="btn facebook" onClick={this.deleteAgencyConnection}>
                    <FaIcon prefix="fab" iconName="facebook-f"/>{t('facebook_link.disconnect_from_facebook_ads')}
                </button>
            )
        }
    }
}

export const FacebookLinkComponent = withTranslation()(FacebookLinkComponentClass);
export default FacebookLinkComponent;
